import React, { CSSProperties, MouseEventHandler, useCallback, useState } from 'react';

import { IconName } from 'Components/foundation/icons';
import { withTooltip } from 'Components/hoc';
import { TooltipPosition } from 'Components/structural/STooltip/STooltip';
import UFlag from 'Components/unit/UFlag/UFlag';
import UProgressBar from 'Components/unit/UProgressBar/UProgressBar';
import UStatusIndicator, { UStatusType } from 'Components/unit/UStatusIndicator/UStatusIndicator';

import styles from './UDropdownLanguageItem.style';
import UIconButton from '../UIconButton/UIconButton';

const UIconWithTooltip = withTooltip(UIconButton);

type RightIconProps = {
  name: IconName;
  disabled: boolean;
  click: MouseEventHandler;
  tooltipContent: string;
  tooltipPosition: TooltipPosition;
  tooltipEnabled: boolean;
};

export type UDropdownLanguageItemProps = {
  style?: CSSProperties;
  onClick: MouseEventHandler;
  countryCode: string;
  text: string;
  isDefault?: boolean;
  selected?: boolean;
  translationPercent?: number | null;
  defaultLabel?: string;
  switchToLabel?: string;
  status?: UStatusType;
  statusText?: string;
  validLabel?: string;
  iconOverride?: IconName;
  rightIcon?: RightIconProps;
};

const COMPLETE = 100;

export const UDropdownLanguageItem = ({
  style,
  onClick,
  countryCode,
  text,
  isDefault = false,
  selected = false,
  translationPercent = null,
  defaultLabel = '',
  switchToLabel = '',
  status = undefined,
  statusText = undefined,
  validLabel = '',
  iconOverride,
  rightIcon,
}: UDropdownLanguageItemProps) => {
  const [isHover, setIsHover] = useState(false);

  const handleClick: MouseEventHandler = (e) => {
    if (!selected) {
      onClick(e);
    }
  };

  const handleClickRightIcon: MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      rightIcon?.click?.(e);
    },
    [rightIcon],
  );

  const conditionalStyle = selected ? styles.wrapperSelected : isHover ? styles.wrapperHovered : styles.wrapperDefault;

  return (
    <div
      style={{ ...styles.wrapper, ...conditionalStyle, ...style }}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onClick={handleClick}
    >
      <UFlag countryCode={countryCode} size={16} style={styles.flag} />
      <div style={styles.text}>{text}</div>
      {isDefault && <div style={styles.captionText}>{`(${defaultLabel})`}</div>}
      <div style={styles.fill} />

      {translationPercent === COMPLETE ? (
        <div style={styles.indicatorWrapper}>
          {validLabel}
          <UStatusIndicator type="valid" style={styles.indicatorIcon} />
        </div>
      ) : (
        <>
          {translationPercent !== null ? (
            <div style={styles.barContainer}>
              <UProgressBar percent={translationPercent} />
            </div>
          ) : null}
          {selected ? null : isHover ? <div style={styles.hoveredText}>{switchToLabel}</div> : null}
        </>
      )}

      {translationPercent === null && status && (
        <div style={styles.indicatorWrapper}>
          <UStatusIndicator
            type={status}
            size="medium"
            hideText={false}
            text={statusText}
            style={styles.indicatorIcon}
            icon={iconOverride}
          />
        </div>
      )}
      {rightIcon && (
        <UIconWithTooltip
          icon={rightIcon.name}
          disabled={rightIcon.disabled}
          ghost
          size="S"
          onClick={handleClickRightIcon}
          tooltipPosition={rightIcon.tooltipPosition}
          tooltipContent={rightIcon.tooltipContent}
          tooltipEnabled={rightIcon.tooltipEnabled}
          tooltipMaxWidth={400}
        />
      )}
    </div>
  );
};

export default UDropdownLanguageItem;
